import { useMemo } from 'react';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import {
  ActionOutboxEntityType,
  Feature,
  UIReportV2TicketGroupQuery,
} from 'src/WebApiController';

import {
  FilterToolbarGroup,
  FilterToolbarItemId,
  RowFilter,
  SectionFilter,
  useInternalNotesFilter,
} from '../Filters';
import { useDeliveryTypeFilters } from './Filters/useDeliveryTypeFilters';
import { useIsMappedFilters } from './Filters/useIsMappedFilters';
import { usePurchaseCurrencyCodesFilters } from './Filters/usePurchaseCurrencyCodesFilters';
import { usePurchaseIsConsignmentFilters } from './Filters/usePurchaseIsConsignmentFilters';
import { usePurchaseOrderStateFilters } from './Filters/usePurchaseOrderStateFilters';
import { usePurchasePaymentMethodFilters } from './Filters/usePurchasePaymentMethodFilters';
import { usePurchasePaymentStatusFilters } from './Filters/usePurchasePaymentStatusFilters';
import { useTicketStatusFilters } from './Filters/useTicketStatusFilters';
import { useMergedTagFilters } from './Tags/useMergedTagFilters';
import { useCommonEventFilters } from './useCommonEventFilters';
import { usePurchaseInfoFilters } from './usePurchaseInfoFilters';

export const ticketGroupReportMandatoryFiltersToShow: FilterToolbarItemId[] = [
  'purchaseDates',
];

const PurchaseInfoFilterIdsToShow: FilterToolbarItemId[] = [
  'purchaseDates',
  'purchaseVendorId',
  'purchaseVendorAccountIds',
  'purchaseBuyerId',
  'purchaseCancellationDates',
  'purchaseOrderCreatedBy',
];

export function useTicketGroupReportFilters({
  isHorizontalLayout,
}: {
  isHorizontalLayout?: boolean;
}) {
  const { initialQuery, tempQuery, setTempQuery } =
    useFilterQueryContext<UIReportV2TicketGroupQuery>();

  const hasConsignmentDealFeature = useUserHasFeature(Feature.ConsignmentDeal);

  const purchaseInfoFilters = usePurchaseInfoFilters({
    query: tempQuery,
    setQuery: setTempQuery,
    showCancellationDateFilter: false,
    useRelativePresets: true,
  });

  const supportedPurchaseInfoFilters = useMemo(() => {
    const items = purchaseInfoFilters.items.filter((item) =>
      PurchaseInfoFilterIdsToShow.includes(item.filterId)
    );

    return {
      ...purchaseInfoFilters,
      items,
    };
  }, [purchaseInfoFilters]);

  const purchasePaymentStatusFilter = usePurchasePaymentStatusFilters({
    query: tempQuery,
    setQuery: setTempQuery,
  });

  const purchasePaymentMethodFilter = usePurchasePaymentMethodFilters({
    query: tempQuery,
    setQuery: setTempQuery,
  });

  const purchaseOrderStateFilter = usePurchaseOrderStateFilters({
    query: tempQuery,
    setQuery: setTempQuery,
    isHorizontalLayout,
  });

  const purchaseCurrencyCodesFilter = usePurchaseCurrencyCodesFilters({
    query: tempQuery,
    setQuery: setTempQuery,
  });

  const isConsignmentFilter = usePurchaseIsConsignmentFilters({
    query: tempQuery,
    setQuery: setTempQuery,
  });

  const deliveryTypeFilter = useDeliveryTypeFilters({
    query: tempQuery,
    setQuery: setTempQuery,
    allowCustomDeliveryType: true,
  });

  const internalNotesFilter =
    useInternalNotesFilter<UIReportV2TicketGroupQuery>({
      entityType: ActionOutboxEntityType.Purchase,
      isHorizontalLayout,
    });

  const eventFilters = useCommonEventFilters({
    showCatalogFilters: true,
    showEventFilter: true,
    showVenueFilter: true,
    showPerformerFilter: true,
    forceUseCatalogV2: true,
  });

  const tagsFilter = useMergedTagFilters({
    query: tempQuery,
    setQuery: setTempQuery,
    entityType: ActionOutboxEntityType.Purchase,
    includeInReport: true,
  });

  const isMappedFilter = useIsMappedFilters({
    query: tempQuery,
    setQuery: setTempQuery,
  });

  const ticketStatusFilter = useTicketStatusFilters({
    query: tempQuery,
    setQuery: setTempQuery,
  });

  const seatingFilters = useMemo<FilterToolbarGroup>(
    () => ({
      titleContentId: ContentId.Seating,
      type: 'row',
      items: [
        {
          filterId: 'sectionContains',
          labelContentId: ContentId.Section,
          filterQueryKeys: ['sectionEquals', 'sectionContains'],
          filterItem: (
            <SectionFilter
              query={tempQuery}
              setQuery={setTempQuery}
              isHorizontalLayout={isHorizontalLayout}
            />
          ),
        },
        {
          filterId: 'rowContains',
          labelContentId: ContentId.Row,
          filterQueryKeys: ['rowEquals', 'rowContains'],
          filterItem: (
            <RowFilter
              query={tempQuery}
              setQuery={setTempQuery}
              isHorizontalLayout={isHorizontalLayout}
            />
          ),
        },
      ],
    }),
    [isHorizontalLayout, setTempQuery, tempQuery]
  );

  return useMemo(
    () => ({
      filters: [
        eventFilters,
        supportedPurchaseInfoFilters,
        {
          titleContentId: ContentId.Payments,
          type: 'row',
          items: [purchasePaymentStatusFilter, purchasePaymentMethodFilter],
        },
        {
          titleContentId: ContentId.Delivery,
          type: 'row',
          items: [deliveryTypeFilter],
        },
        {
          titleContentId: ContentId.Status,
          type: 'row',
          items: [
            purchaseOrderStateFilter,
            ...(hasConsignmentDealFeature ? [isConsignmentFilter] : []),
            ticketStatusFilter,
          ],
        },
        {
          titleContentId: ContentId.Cost,
          type: 'row',
          items: [purchaseCurrencyCodesFilter],
        },
        {
          titleContentId: ContentId.Mappings,
          type: 'row',
          items: [isMappedFilter],
        },
        seatingFilters,
        ...(internalNotesFilter ? [internalNotesFilter] : []),
        ...tagsFilter,
      ],
    }),
    [
      eventFilters,
      supportedPurchaseInfoFilters,
      purchasePaymentStatusFilter,
      purchasePaymentMethodFilter,
      deliveryTypeFilter,
      purchaseOrderStateFilter,
      hasConsignmentDealFeature,
      isConsignmentFilter,
      ticketStatusFilter,
      purchaseCurrencyCodesFilter,
      isMappedFilter,
      seatingFilters,
      internalNotesFilter,
      tagsFilter,
    ]
  );
}
